import { IBucketResponseSchema } from '@monetize/types/app';
import { FilterType } from '@monetize/types/common';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useDeleteEntity, useGetListData } from '../../../api/queryUtils';
import {
  MHStack,
  MPageContainer,
  MPageSearchInput,
} from '../../../components/Monetize';
import DataTableActions from '../../../components/Monetize/DataTable/MDataTableActions';
import { useTableColumns } from '../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import { ACTION_COLUMN_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import { ROUTES } from '../../../constants';
import { getBucketEditRoute } from '../../../constants/routes';
import { useSettingsReadOnlyRule } from '../../../hooks/useSettingsReadOnlyRule';
import {
  tableFilterSelector,
  tablePagerSelector,
  tableSearchTermSelector,
} from '../../../store/global.store';
import {
  FilterStateKeys,
  getDefaultFilter,
  getDefaultPager,
} from '../../../store/store.types';
import {
  buildFilterParamsRequestObject,
  getIsTrulyEmptyList,
  transformTableFilterValue,
} from '../../../utils';
import { navigateOrOpenNewWindow } from '../../../utils/misc';
import PageHeader from '../components/PageHeader';
import TableHeader from '../components/TableHeader';
import {
  BUCKET_LIST_CATALOG,
  BucketListColumnContext,
} from './bucketList.columns';

const BucketListPage = () => {
  const navigate = useNavigate();
  const [initialFilters, persistTableFilter] = useAtom(
    tableFilterSelector(FilterStateKeys.BUCKET_LIST),
  );

  const [pager, setPager] = useAtom(
    tablePagerSelector(FilterStateKeys.BUCKET_LIST),
  );

  const [searchTerm, setSearchTerm] = useAtom(
    tableSearchTermSelector(FilterStateKeys.BUCKET_LIST),
  );

  const [filters, setFilters] = useState<FilterType[]>(initialFilters);
  const [searchKey] = useState<string>('name');
  const filterParams = useMemo(() => {
    const params = buildFilterParamsRequestObject(
      transformTableFilterValue(filters),
      searchTerm,
      searchKey,
    );
    return params;
  }, [filters, searchTerm, searchKey]);

  const tableRef = useRef<DataTableRef>(null);

  const {
    data: bucketList,
    isLoading,
    isFetched,
    isRefetching,
    refetch,
  } = useGetListData<IBucketResponseSchema>('productCatalogBuckets', {
    config: pager,
    filters: filterParams,
  });

  const { mutateAsync: doDeleteBucket } =
    useDeleteEntity<IBucketResponseSchema>('productCatalogBuckets', {
      meta: { showErrorToast: true },
    });

  useEffect(() => {
    persistTableFilter(filters);
  }, [filters, persistTableFilter]);

  const loading = isLoading || (isRefetching && !isFetched);

  const isTrulyEmptyList = getIsTrulyEmptyList({
    loading,
    totalElements: bucketList?.totalElements || 0,
    filters,
    searchTerm,
    page: pager.page,
  });

  const onResetFilter = () => {
    setFilters(getDefaultFilter(FilterStateKeys.BUCKET_LIST));
    setPager(getDefaultPager(FilterStateKeys.BUCKET_LIST));
    tableRef.current?.resetFilters();
  };

  const onSelectEdit = useCallback(
    (id: string, openInNewWindow = false) => {
      openInNewWindow
        ? window.open(ROUTES.getBucketEditRoute(id), '_blank')
        : navigate(ROUTES.getBucketEditRoute(id));
    },
    [navigate],
  );
  const { canCreateSettings } = useSettingsReadOnlyRule();

  const actionBodyTemplate = useCallback(
    (rowData: IBucketResponseSchema) => {
      const { id, locked, name } = rowData;
      if (!id || !canCreateSettings) {
        return null;
      }

      const confirmProps = {
        title: `Are you sure you want to delete "${name}"?`,
        description: 'Once deleted, this bucket will no longer be available.',
      };

      const actions = [
        {
          title: 'Edit',
          enabled: true,
          action: () => onSelectEdit(id),
        },

        {
          title: 'Delete',
          color: 'danger',
          confirmProps: {
            ...confirmProps,
            yesButton: 'Delete',
          },
          enabled: !locked,
          action: () => doDeleteBucket({ id }),
        },
      ];

      return <DataTableActions actions={actions} />;
    },
    [canCreateSettings, doDeleteBucket, onSelectEdit],
  );

  const searchComponentReset = React.useRef<any>(null);

  const columnContext = useMemo<BucketListColumnContext>(
    () => ({ actionBodyTemplate }),
    [actionBodyTemplate],
  );
  const { columns, state: columnState } = useTableColumns(
    BUCKET_LIST_CATALOG,
    columnContext,
  );

  return (
    <MPageContainer enableAccountSearch>
      <PageHeader />

      <TableHeader
        model="Bucket"
        showCreateNewBtn={!isTrulyEmptyList && canCreateSettings}
      >
        {!isTrulyEmptyList && (
          <MHStack gap="2">
            <MPageSearchInput
              placeholderKey="Name"
              value={searchTerm}
              onChange={setSearchTerm}
              count={bucketList?.totalElements}
              resetSearch={searchComponentReset}
              mode="alwaysExpanded"
            />
            <RefreshTableButton refetch={refetch} />
            <ExportEntityButton
              entity="buckets"
              filters={filters}
              searchKey={searchKey}
              searchTerm={searchTerm}
              sortField={pager.sortField}
              sortOrder={pager.sortOrder}
            />
          </MHStack>
        )}
      </TableHeader>

      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={bucketList?.content ?? []}
        enablePagination
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={bucketList?.totalElements}
        totalPages={bucketList?.totalPages}
        pager={pager}
        setPager={setPager}
        initialFilters={filters}
        onFiltersChange={(newFilters) => {
          setFilters(newFilters);
        }}
        onRowClick={(e, _tableRow, openInNewWindow) =>
          navigateOrOpenNewWindow(
            navigate,
            getBucketEditRoute(e.id),
            openInNewWindow,
          )
        }
        isLoading={loading}
        emptyProps={{
          mainMessage: 'Looks like there are no buckets here.',
          smallMessage: null,
          btnLabel: canCreateSettings ? 'New Bucket' : '',
          to: ROUTES.BUCKET_NEW,
        }}
        emptyFilterResultsProps={{
          btnLabel: 'Reset Filters',
        }}
        resetFilter={onResetFilter}
      />
    </MPageContainer>
  );
};

export default BucketListPage;
